import PropTypes from 'prop-types';
import { addDays, subDays, isBefore } from 'date-fns';

import { trans, useSoyuzExperiment } from '@spotahome/soyuz/client';

import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import { useRef, useState } from 'react';

import { capitalizeFirstLetter } from '../../utils/stringUtils';

import FormDateLegacy from '../../FormDate/FormDateLegacy';
import FormRadioButton from '../../FormRadioButton';

import {
  getRentalTypeRanges,
  SHORT_TERM,
  MID_TERM,
  LONG_TERM
} from '../../utils/rentalType';

import './AvailabilityFilter.scss';
import { formatDate, getDateAsUTC } from '../../utils/dateUtils';

import FlexibleOptions, {
  EXACT_DATES_OFFSET,
  THREE_WEEKS_OFFSET
} from './FlexibleOptions';
import { getSelectedDaysOffset } from './utils';

const AvailabilityFilter = ({
  from,
  to,
  minimumStaying,
  updateFiltersWith,
  rentalType,
  disablePrice = false,
  moveOutFrom = '',
  moveOutTo = '',
  moveInFrom = '',
  moveInTo = ''
}) => {
  const today = getDateAsUTC(new Date());
  const todayDateFormatted = formatDate(today);
  const minMoveOutDate = getDateAsUTC(new Date(from || todayDateFormatted));
  const offsetMinMoveOutDate = addDays(minMoveOutDate, minimumStaying);

  const moveInOffsetRef = useRef(
    Math.max(
      getSelectedDaysOffset(from, moveInFrom, THREE_WEEKS_OFFSET),
      getSelectedDaysOffset(from, moveInTo, THREE_WEEKS_OFFSET)
    )
  );
  const previousMoveInOffset = useRef(moveInOffsetRef.current);

  const moveOutOffsetRef = useRef(
    Math.max(
      getSelectedDaysOffset(to, moveOutFrom, EXACT_DATES_OFFSET),
      getSelectedDaysOffset(to, moveOutTo, EXACT_DATES_OFFSET)
    )
  );
  const previousMoveOutOffset = useRef(moveOutOffsetRef.current);

  const [isEnhancedDatesExperiment, setIsEnhancedDatesExperiment] =
    useState(undefined);
  const EnhancedDatesExperiment = useSoyuzExperiment(
    'EnhancedDatesSearchExperience'
  );

  // TODO: Check for cities with dynamic minimum stay to avoid defaulting to 1 month in the future.

  const rentalTypeRanges = getRentalTypeRanges(minimumStaying);

  const moveOutValues = [
    {
      value: SHORT_TERM,
      exposed: `${capitalizeFirstLetter(
        trans('rentalDurationType.shortTerm')
      )}`,
      text: `${rentalTypeRanges[SHORT_TERM].from}-${
        rentalTypeRanges[SHORT_TERM].to
      } ${trans('date.months')}`
    },
    {
      value: MID_TERM,
      exposed: `${capitalizeFirstLetter(trans('rentalDurationType.midTerm'))}`,
      text: `${rentalTypeRanges[MID_TERM].from}-${
        rentalTypeRanges[MID_TERM].to
      } ${trans('date.months')}`
    },
    {
      value: LONG_TERM,
      exposed: `${capitalizeFirstLetter(trans('rentalDurationType.longTerm'))}`,
      text: `${rentalTypeRanges[LONG_TERM].from}+ ${trans('date.months')}`
    }
  ];

  const handleMoveInDateSelected = ({ value }) => {
    SoyuzAnalytics.sendGA4Event('filter', {
      context: 'availabilities',
      section: 'move-in',
      value
    });

    const offsetNewMoveIn = addDays(
      getDateAsUTC(new Date(value)),
      minimumStaying
    );
    let updatedMoveOut = to;

    if (isBefore(getDateAsUTC(new Date(to)), offsetNewMoveIn)) {
      updatedMoveOut = formatDate(offsetNewMoveIn);
    }

    previousMoveInOffset.current = moveInOffsetRef.current;
    previousMoveOutOffset.current = moveOutOffsetRef.current;

    updateFiltersWith({
      'move-in': value,
      'move-out': updatedMoveOut,
      moveInFrom: formatDate(
        subDays(getDateAsUTC(new Date(value)), moveInOffsetRef.current)
      ),
      moveInTo: formatDate(
        addDays(getDateAsUTC(new Date(value)), moveInOffsetRef.current)
      ),
      moveOutFrom:
        moveOutOffsetRef.current > 0
          ? formatDate(
              subDays(
                getDateAsUTC(new Date(updatedMoveOut)),
                moveOutOffsetRef.current
              )
            )
          : '',
      moveOutTo:
        moveOutOffsetRef.current > 0
          ? formatDate(
              addDays(
                getDateAsUTC(new Date(updatedMoveOut)),
                moveOutOffsetRef.current
              )
            )
          : ''
    });
  };

  const handleTodaySelected = () => {
    SoyuzAnalytics.sendGA4Event('filter', {
      context: 'availabilities',
      section: 'today'
    });
    const offsetNewMoveIn = addDays(today, minimumStaying);
    let updatedMoveOut = to;

    if (isBefore(getDateAsUTC(new Date(to)), offsetNewMoveIn)) {
      updatedMoveOut = formatDate(offsetNewMoveIn);
    }

    updateFiltersWith({
      'move-in': todayDateFormatted,
      moveInFrom: todayDateFormatted,
      moveInTo: formatDate(
        addDays(
          getDateAsUTC(new Date(todayDateFormatted)),
          moveInOffsetRef.current
        )
      ),
      'move-out': updatedMoveOut,
      moveOutFrom: '',
      moveOutTo: ''
    });
  };

  const handleMoveOutDateSelected = ({ value }) => {
    SoyuzAnalytics.sendGA4Event('filter', {
      context: 'availabilities',
      section: 'move-out',
      value
    });

    previousMoveOutOffset.current = moveOutOffsetRef.current;

    updateFiltersWith({
      'move-out': value,
      'rentalType[]': [],
      moveOutFrom:
        moveOutOffsetRef.current > 0
          ? formatDate(
              subDays(getDateAsUTC(new Date(value)), moveOutOffsetRef.current)
            )
          : '',
      moveOutTo:
        moveOutOffsetRef.current > 0
          ? formatDate(
              addDays(getDateAsUTC(new Date(value)), moveOutOffsetRef.current)
            )
          : ''
    });
  };

  const handleMoveOutRangeSelected = value => {
    let newRentalType;
    if (rentalType.includes(value)) {
      SoyuzAnalytics.sendGA4Event('filter', {
        context: 'availabilities',
        section: 'move-out-range',
        action: 'remove',
        value
      });
      newRentalType = rentalType.filter(option => option !== value);
    } else {
      SoyuzAnalytics.sendGA4Event('filter', {
        context: 'availabilities',
        section: 'move-out-range',
        action: 'add',
        value
      });
      newRentalType = [...rentalType, value];
    }

    updateFiltersWith({
      'rentalType[]': newRentalType,
      'move-out': '',
      moveOutFrom: '',
      moveOutTo: ''
    });
  };

  const isTodayDateSelected = () => todayDateFormatted === from;

  const isRentalTypeSelected = () => rentalType.length;

  const moveInFlexibleOptionsChangeHandler = value => {
    SoyuzAnalytics.sendGA4Event('filter', {
      context: 'availabilities',
      section: 'move-in-flexible-options',
      value
    });
    moveInOffsetRef.current = value;
  };

  const handleCalendarOpen = () => {
    if (isEnhancedDatesExperiment === undefined) {
      setIsEnhancedDatesExperiment(
        EnhancedDatesExperiment.get('behaviour') === 'variant'
      );
    }
  };

  const handleMoveInCalendarClose = () => {
    if (previousMoveInOffset.current !== moveInOffsetRef.current) {
      handleMoveInDateSelected({ value: from });
    }
  };

  const moveOutFlexibleOptionsChangeHandler = value => {
    SoyuzAnalytics.sendGA4Event('filter', {
      context: 'availabilities',
      section: 'move-out-flexible-options',
      value
    });
    moveOutOffsetRef.current = value;
  };

  const handleMoveOutCalendarClose = () => {
    if (previousMoveOutOffset.current !== moveOutOffsetRef.current) {
      handleMoveOutDateSelected({ value: to });
    }
  };

  const renderMoveInCalendarContent = () =>
    isEnhancedDatesExperiment && (
      <FlexibleOptions
        className="availability-filter__options"
        onChange={moveInFlexibleOptionsChangeHandler}
        value={moveInOffsetRef.current}
      />
    );

  const renderMoveOutCalendarContent = () =>
    isEnhancedDatesExperiment && (
      <FlexibleOptions
        className="availability-filter__options"
        onChange={moveOutFlexibleOptionsChangeHandler}
        value={moveOutOffsetRef.current}
      />
    );

  return (
    <div className="availability-filter">
      <div className="availability-filter__title">
        {trans('search.exposedfilters.dates.move-in')}
      </div>

      <div className="availability-filter__button-container">
        <FormRadioButton
          onClick={handleTodaySelected}
          value="asap"
          selected={isTodayDateSelected()}
        >
          <div className="availability-filter__button-text">
            {trans('search.exposedfilters.dates.asap')}
          </div>
        </FormRadioButton>
        <FormDateLegacy
          onChange={handleMoveInDateSelected}
          onCalendarClose={handleMoveInCalendarClose}
          value={!isTodayDateSelected() && from}
          absolutePosition
          calendarBordered
          minDate={today}
          renderCalendarContent={renderMoveInCalendarContent}
          onCalendarOpen={handleCalendarOpen}
          closeOnSelect={!isEnhancedDatesExperiment}
        >
          {({
            renderCalendar,
            displayRef,
            triggerRef,
            humanReadableDate,
            selectedDate,
            onClick
          }) => (
            <>
              <div ref={triggerRef}>
                <FormRadioButton
                  value="move-in"
                  selected={
                    selectedDate &&
                    !isTodayDateSelected() &&
                    from === selectedDate
                  }
                  disabled={false}
                  onClick={onClick}
                >
                  <span
                    ref={displayRef}
                    className="availability-filter__button-text"
                  >
                    {humanReadableDate ||
                      trans('search.exposedfilters.dates.select_date')}
                  </span>
                </FormRadioButton>
              </div>
              {renderCalendar()}
            </>
          )}
        </FormDateLegacy>
      </div>
      <div className="availability-filter__title">
        {trans('search.exposedfilters.dates.move-out')}
      </div>
      <div className="availability-filter__button-container">
        {moveOutValues.map(({ value, text }) => (
          <FormRadioButton
            key={value}
            value={value}
            selected={rentalType.includes(value) && !to}
            onClick={handleMoveOutRangeSelected}
          >
            <div className="availability-filter__button-text">{text}</div>
          </FormRadioButton>
        ))}
        <FormDateLegacy
          onChange={handleMoveOutDateSelected}
          onCalendarClose={handleMoveOutCalendarClose}
          value={!isRentalTypeSelected() && to}
          disabled={!from && !moveInFrom && !moveInTo}
          absolutePosition
          calendarBordered
          minDate={offsetMinMoveOutDate}
          calendarPosition={to || offsetMinMoveOutDate}
          renderCalendarContent={renderMoveOutCalendarContent}
          onCalendarOpen={handleCalendarOpen}
          closeOnSelect={!isEnhancedDatesExperiment}
        >
          {({
            renderCalendar,
            displayRef,
            triggerRef,
            humanReadableDate,
            onClick
          }) => (
            <>
              <div ref={triggerRef}>
                <FormRadioButton
                  value="move-out"
                  disabled={!from && !moveInFrom && !moveInTo}
                  selected={!rentalType.length && to}
                  onClick={onClick}
                >
                  <div
                    ref={displayRef}
                    className="availability-filter__button-text"
                  >
                    {humanReadableDate ||
                      trans('search.exposedfilters.dates.specific_move_out')}
                  </div>
                </FormRadioButton>
              </div>
              {renderCalendar()}
            </>
          )}
        </FormDateLegacy>
      </div>
      {disablePrice && <p>{trans('search.hide-prices-until-dates')}</p>}
    </div>
  );
};

AvailabilityFilter.propTypes = {
  updateFiltersWith: PropTypes.func.isRequired,
  from: PropTypes.string.isRequired,
  to: PropTypes.string.isRequired,
  minimumStaying: PropTypes.number.isRequired,
  disablePrice: PropTypes.bool,
  rentalType: PropTypes.arrayOf(PropTypes.string).isRequired,
  moveOutFrom: PropTypes.string,
  moveOutTo: PropTypes.string,
  moveInFrom: PropTypes.string,
  moveInTo: PropTypes.string
};

export default AvailabilityFilter;
